import _ from 'lodash';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { ForecastType, ResultType } from '@/tools/signalForecast/signalForecast.constants';
import type { BaseToolPersistedState, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export const DEFAULT_DURATION = { value: 1, units: 'day' };

interface SignalForecastState extends BaseToolPersistedState {
  trainingDuration: ValueWithUnitsItem;
  blendWindow: ValueWithUnitsItem;
  period: ValueWithUnitsItem;
  forecastDuration: ValueWithUnitsItem;
  griddingPeriod: ValueWithUnitsItem;
  forecastType: ForecastType;
  resultType: ResultType;
  forceThroughLastSample: boolean;
  resampleTargetSignal: boolean;
  isSeasonal: boolean;
  color: string;
}

export type SignalForecastDehydratedState = SignalForecastState;

export class SignalForecastStore extends BaseToolStore {
  static readonly storeName = 'sqSignalForecastsStore';
  type = TREND_TOOLS.SIGNAL_FORECAST;
  parameterDefinitions = {
    forecastSignal: { predicate: ['name', 'signal2'] },
    runCycleCondition: { predicate: ['name', 'condition'] },
    targetSignal: {
      predicate: (parameter: ToolParameter) => {
        const name = parameter.name ?? '';
        return !_.includes(['condition', 'signal2'], name);
      },
    },
  };

  initialize(): void {
    const initialState: SignalForecastState = {
      ...BASE_TOOL_COMMON_PROPS,
      trainingDuration: { value: 7, units: 'days' },
      blendWindow: { value: 0, units: 'min' },
      period: DEFAULT_DURATION,
      forecastDuration: DEFAULT_DURATION,
      griddingPeriod: DEFAULT_DURATION,
      forecastType: ForecastType.LINEAR,
      resultType: ResultType.FORECAST,
      forceThroughLastSample: false,
      resampleTargetSignal: false,
      isSeasonal: false,
      color: '',
    };
    this.state = this.immutable(initialState);
  }

  get targetSignal(): ToolItem & { id: string } {
    return this.state.get('targetSignal');
  }

  get runCycleCondition(): ToolItem & { id: string } {
    return this.state.get('runCycleCondition');
  }

  get forecastSignal(): ToolItem & { id: string } {
    return this.state.get('forecastSignal');
  }

  get forecastType(): ForecastType {
    return this.state.get('forecastType');
  }

  get period(): ValueWithUnitsItem {
    return this.state.get('period');
  }

  get griddingPeriod(): ValueWithUnitsItem {
    return this.state.get('griddingPeriod');
  }

  get resampleTargetSignal(): boolean {
    return this.state.get('resampleTargetSignal');
  }

  get isSeasonal(): boolean {
    return this.state.get('isSeasonal');
  }

  get color(): string {
    return this.state.get('color');
  }

  get forceThroughLastSample(): boolean {
    return this.state.get('forceThroughLastSample');
  }

  get resultType(): ResultType {
    return this.state.get('resultType');
  }

  get trainingDuration(): ValueWithUnitsItem {
    return this.state.get('trainingDuration');
  }

  get forecastDuration(): ValueWithUnitsItem {
    return this.state.get('forecastDuration');
  }

  get blendWindow(): ValueWithUnitsItem {
    return this.state.get('blendWindow');
  }

  dehydrate(): SignalForecastDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<SignalForecastDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingDuration(payload: { trainingDuration: ValueWithUnitsItem }): void {
    this.state.set('trainingDuration', payload.trainingDuration);
  }

  setForecastDuration(payload: { forecastDuration: ValueWithUnitsItem }): void {
    this.state.set('forecastDuration', payload.forecastDuration);
  }

  setBlendWindow(payload: { blendWindow: ValueWithUnitsItem }): void {
    this.state.set('blendWindow', payload.blendWindow);
  }

  setForecastType(payload: { forecastType: ForecastType }): void {
    this.state.set('forecastType', payload.forecastType);
  }

  setPeriod(payload: { period: ValueWithUnitsItem }): void {
    this.state.set('period', payload.period);
  }

  setGriddingPeriod(payload: { griddingPeriod: ValueWithUnitsItem }): void {
    this.state.set('griddingPeriod', payload.griddingPeriod);
  }

  setResampleTargetSignal(payload: { resampleTargetSignal: boolean }): void {
    this.state.set('resampleTargetSignal', payload.resampleTargetSignal);
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  setForceThroughLastSample(payload: { forceThroughLastSample: boolean }): void {
    this.state.set('forceThroughLastSample', payload.forceThroughLastSample);
  }

  setIsSeasonal(payload: { isSeasonal: boolean }): void {
    this.state.set('isSeasonal', payload.isSeasonal);
  }

  setResultType(payload: { resultType: ResultType }): void {
    this.state.set('resultType', payload.resultType);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    SF_SET_TRAINING_DURATION: this.setTrainingDuration,
    SF_SET_FORECAST_DURATION: this.setForecastDuration,
    SF_SET_BLEND_WINDOW: this.setBlendWindow,
    SF_SET_FORECAST_TYPE: this.setForecastType,
    SF_SET_COLOR: this.setColor,
    SF_SET_PERIOD: this.setPeriod,
    SF_SET_IS_SEASONAL: this.setIsSeasonal,
    SF_SET_RESAMPLE_TARGET_SIGNAL: this.setResampleTargetSignal,
    SF_SET_GRIDDING_PERIOD: this.setGriddingPeriod,
    SF_SET_FORCE_THROUGH_LAST_SAMPLE: this.setForceThroughLastSample,
    SF_SET_RESULT_TYPE: this.setResultType,
  };
}
